<script setup>
// Potansiyel musteri (lead) formlari: listeleme ve yeni form olusturma.
//
// Formlar reklam hesabina degil SAYFA'ya bagli. Listeleme araci sayfa kimligi
// bekleyebildigi icin ayri bir alan konuldu; alan bosken yalnizca hesap
// gonderilip aracin kendi varsayilanina birakiliyor - uydurma bir sayfa
// kimligi gondermektense hatayi kullaniciya gostermek dogru.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import { aracCalistir, diziyeCevir } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
});

const { t } = useI18n();
const route = useRoute();

const satirlar = ref([]);
const yukleniyor = ref(false);
const hata = ref('');
const sayfaId = ref('');

const formAcik = ref(false);
const kaydediliyor = ref(false);
const form = ref({
  page_id: '',
  name: '',
  privacy_policy_url: '',
  follow_up_action_url: '',
});

// Meta tarihi ISO metni olarak dondurur ("2024-05-02T10:11:12+0000"); tabloda
// oldugu gibi gostermek okunmuyordu.
const tarihBicimle = deger => {
  if (!deger) return '—';
  const tarih = new Date(deger);
  return Number.isNaN(tarih.getTime())
    ? String(deger)
    : tarih.toLocaleDateString('tr-TR');
};

const sutunlar = computed(() => [
  { anahtar: 'id', baslik: t('META_ADS.FORMLAR.SUTUN.ID') },
  { anahtar: 'name', baslik: t('META_ADS.FORMLAR.SUTUN.AD') },
  { anahtar: 'status', baslik: t('META_ADS.FORMLAR.SUTUN.DURUM') },
  { anahtar: 'leads_count', baslik: t('META_ADS.FORMLAR.SUTUN.LEAD') },
  {
    anahtar: 'created_time',
    baslik: t('META_ADS.FORMLAR.SUTUN.TARIH'),
    bicim: tarihBicimle,
  },
]);

const yukle = async () => {
  if (!props.hesap) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const argumanlar = { account_id: props.hesap };
    if (sayfaId.value.trim()) argumanlar.page_id = sayfaId.value.trim();
    const sonuc = await aracCalistir(
      route.params.accountId,
      'ads_get_lead_forms',
      argumanlar
    );
    satirlar.value = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
    satirlar.value = [];
  } finally {
    yukleniyor.value = false;
  }
};

const formOlustur = async () => {
  if (!window.confirm(t('META_ADS.FORMLAR.OLUSTUR_ONAY'))) return;
  kaydediliyor.value = true;
  hata.value = '';
  try {
    // Bos alan gonderilmiyor; Meta bos gizlilik politikasi adresini
    // dogrulama hatasi olarak dondurur ve hata mesaji anlasilmaz olur.
    const argumanlar = { account_id: props.hesap };
    Object.entries(form.value).forEach(([anahtar, deger]) => {
      if (String(deger || '').trim()) argumanlar[anahtar] = String(deger).trim();
    });
    await aracCalistir(
      route.params.accountId,
      'ads_create_lead_form',
      argumanlar,
      { onaylandi: true }
    );
    useAlert(t('META_ADS.FORMLAR.OLUSTU'));
    formAcik.value = false;
    form.value = {
      page_id: '',
      name: '',
      privacy_policy_url: '',
      follow_up_action_url: '',
    };
    await yukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    kaydediliyor.value = false;
  }
};

watch(() => props.hesap, yukle);
onMounted(yukle);
</script>

<template>
  <div class="p-6">
    <p v-if="!hesap" class="text-sm text-n-slate-11">
      {{ t('META_ADS.ORTAK.HESAP_SECIN') }}
    </p>

    <template v-else>
      <div class="flex flex-wrap items-end gap-2 mb-4">
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.FORMLAR.SAYFA_ID') }}
          <input
            v-model="sayfaId"
            type="text"
            class="!mb-0 min-w-[220px]"
            :placeholder="t('META_ADS.FORMLAR.SAYFA_ID_IPUCU')"
            @keyup.enter="yukle"
          />
        </label>
        <span class="grow" />
        <Button
          size="sm"
          variant="faded"
          icon="i-lucide-refresh-cw"
          :label="t('META_ADS.ORTAK.YENILE')"
          @click="yukle"
        />
        <Button
          size="sm"
          variant="solid"
          icon="i-lucide-plus"
          :label="t('META_ADS.FORMLAR.YENI')"
          @click="formAcik = !formAcik"
        />
      </div>

      <p v-if="hata" class="mb-3 text-sm text-n-ruby-11">{{ hata }}</p>

      <form
        v-if="formAcik"
        class="grid gap-3 p-4 mb-5 border rounded-lg sm:grid-cols-2 border-n-weak"
        @submit.prevent="formOlustur"
      >
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.FORMLAR.FORM.SAYFA_ID') }}
          <input v-model="form.page_id" type="text" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.FORMLAR.FORM.AD') }}
          <input v-model="form.name" type="text" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.FORMLAR.FORM.GIZLILIK') }}
          <input v-model="form.privacy_policy_url" type="text" placeholder="https://" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.FORMLAR.FORM.TESEKKUR') }}
          <input v-model="form.follow_up_action_url" type="text" placeholder="https://" />
        </label>
        <p class="text-xs sm:col-span-2 text-n-slate-11">
          {{ t('META_ADS.FORMLAR.FORM.IPUCU') }}
        </p>
        <div class="flex gap-2 sm:col-span-2">
          <Button
            size="sm"
            variant="solid"
            type="submit"
            :is-loading="kaydediliyor"
            :label="t('META_ADS.ORTAK.KAYDET')"
          />
          <Button
            type="button"
            size="sm"
            variant="faded"
            :label="t('META_ADS.ORTAK.VAZGEC')"
            @click="formAcik = false"
          />
        </div>
      </form>

      <AracTablosu
        :satirlar="satirlar"
        :sutunlar="sutunlar"
        :yukleniyor="yukleniyor"
        :bos-metin="t('META_ADS.FORMLAR.BOS')"
      />
    </template>
  </div>
</template>
